<script lang="ts">
  import {
    ArrowLeft,
    Activity,
    BarChart3,
    Flame,
    Users,
    CandlestickChart,
    Clock,
    Repeat,
    GraduationCap,
    PlayIcon,
    OctagonPauseIcon,
  } from "lucide-svelte";
  import { route } from "@mateothegreat/svelte5-router";
  import { STORE } from "../../state.svelte";
  import type { Snippet } from "svelte";

  let {
    activePage,
    activeTab = "",
    onTabChange,
    isRunning = false,
    onToggleStream,
    children,
  }: {
    activePage: "streaming" | "historic" | "graduations" | "wrap-sol";
    activeTab?: string;
    onTabChange?: (tab: string) => void;
    isRunning?: boolean;
    onToggleStream?: () => void;
    children: Snippet;
  } = $props();

  const STREAMING_TABS = [
    { id: "Live Trades", icon: Activity },
    { id: "Top Pools", icon: BarChart3 },
    { id: "Curve Pools", icon: Flame },
    { id: "Wallets", icon: Users },
    { id: "Pool View", icon: CandlestickChart },
  ];
</script>

<div class="min-h-screen bg-black text-gray-300 selection:bg-sky-900 selection:text-white overflow-x-hidden">
  <!-- Top bar -->
  <div class="border-b border-gray-800 px-4 py-3 flex items-center justify-between">
    <a use:route href="/products/sailfish" class="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-white transition-colors">
      <ArrowLeft class="size-4" /> Back to Sailfish
    </a>
    <h1 class="text-sm font-bold text-white tracking-tight hidden sm:block">Sailfish Solana</h1>
    <div class="badge badge-outline rounded-none uppercase tracking-widest font-mono text-[9px] text-sky-500 border-sky-500/50 px-2 py-1">
      Solana
    </div>
  </div>

  <!-- Mobile nav -->
  <div class="md:hidden w-full border-b border-gray-800 overflow-x-auto flex items-center gap-1.5 px-2 py-2 bg-black sticky top-[52px] z-10">
    {#if activePage === "streaming"}
      {#each STREAMING_TABS as tab}
        {@const disabled = !isRunning}
        <button
          class="btn btn-sm shrink-0 rounded-sm transition-colors whitespace-nowrap text-xs
            {activeTab === tab.id
              ? 'bg-sky-600 text-white border-sky-600'
              : disabled
                ? 'bg-gray-900/50 border border-gray-800 text-gray-700 cursor-not-allowed'
                : 'bg-gray-900/50 border border-gray-800 text-gray-400'}"
          onclick={() => { if (!disabled && onTabChange) onTabChange(tab.id); }}
        >
          {tab.id}
        </button>
      {/each}
      <div class="w-px h-5 bg-gray-800 shrink-0 mx-1"></div>
    {/if}
    <a use:route href="/tools/solana/historic"
      class="btn btn-sm shrink-0 rounded-sm whitespace-nowrap text-xs {activePage === 'historic' ? 'bg-sky-600 text-white border-sky-600' : 'bg-gray-900/50 border border-gray-800 text-gray-400'}">
      Trades
    </a>
    <a use:route href="/tools/solana/graduations"
      class="btn btn-sm shrink-0 rounded-sm whitespace-nowrap text-xs {activePage === 'graduations' ? 'bg-sky-600 text-white border-sky-600' : 'bg-gray-900/50 border border-gray-800 text-gray-400'}">
      Grads
    </a>
    <a use:route href="/tools/solana/wrap-sol"
      class="btn btn-sm shrink-0 rounded-sm whitespace-nowrap text-xs {activePage === 'wrap-sol' ? 'bg-sky-600 text-white border-sky-600' : 'bg-gray-900/50 border border-gray-800 text-gray-400'}">
      Wrap SOL
    </a>
  </div>

  <div class="flex">
    <!-- Desktop Sidebar -->
    <aside class="hidden md:flex flex-col w-52 shrink-0 border-r border-gray-800 min-h-[calc(100vh-52px)] sticky top-[52px] bg-black">
      <!-- Streaming section -->
      <div class="px-3 pt-3">
        <p class="text-[9px] font-mono text-gray-600 uppercase tracking-[0.3em] mb-2 px-2">Streaming</p>
        {#each STREAMING_TABS as tab}
          {@const Icon = tab.icon}
          {#if activePage === "streaming"}
            {@const disabled = false}
            <button
              class="flex items-center gap-2.5 w-full px-2 py-2 text-xs font-mono rounded-sm transition-colors
                {activeTab === tab.id
                  ? 'border-l-2 border-sky-500 bg-sky-500/5 text-white pl-[6px]'
                  : disabled
                    ? 'text-gray-700 cursor-not-allowed'
                    : 'text-gray-500 hover:text-white hover:bg-gray-900/50'}"
              onclick={() => { if (!disabled && onTabChange) onTabChange(tab.id); }}
            >
              <Icon class="size-3.5" />
              {tab.id}
            </button>
          {:else}
            <a
              use:route
              href="/tools/solana"
              class="flex items-center gap-2.5 w-full px-2 py-2 text-xs font-mono text-gray-500 hover:text-white hover:bg-gray-900/50 rounded-sm transition-colors"
            >
              <Icon class="size-3.5" />
              {tab.id}
            </a>
          {/if}
        {/each}
      </div>

      <!-- Data section -->
      <div class="px-3 pt-5">
        <p class="text-[9px] font-mono text-gray-600 uppercase tracking-[0.3em] mb-2 px-2">Data</p>
        <a
          use:route
          href="/tools/solana/historic"
          class="flex items-center gap-2.5 w-full px-2 py-2 text-xs font-mono rounded-sm transition-colors
            {activePage === 'historic'
              ? 'border-l-2 border-sky-500 bg-sky-500/5 text-white pl-[6px]'
              : 'text-gray-500 hover:text-white hover:bg-gray-900/50'}"
        >
          <Clock class="size-3.5" />
          Historic Trades
        </a>
        <a
          use:route
          href="/tools/solana/graduations"
          class="flex items-center gap-2.5 w-full px-2 py-2 text-xs font-mono rounded-sm transition-colors
            {activePage === 'graduations'
              ? 'border-l-2 border-sky-500 bg-sky-500/5 text-white pl-[6px]'
              : 'text-gray-500 hover:text-white hover:bg-gray-900/50'}"
        >
          <GraduationCap class="size-3.5" />
          Graduations
        </a>
      </div>

      <!-- Tools section -->
      <div class="px-3 pt-5">
        <p class="text-[9px] font-mono text-gray-600 uppercase tracking-[0.3em] mb-2 px-2">Tools</p>
        <a
          use:route
          href="/tools/solana/wrap-sol"
          class="flex items-center gap-2.5 w-full px-2 py-2 text-xs font-mono rounded-sm transition-colors
            {activePage === 'wrap-sol'
              ? 'border-l-2 border-sky-500 bg-sky-500/5 text-white pl-[6px]'
              : 'text-gray-500 hover:text-white hover:bg-gray-900/50'}"
        >
          <Repeat class="size-3.5" />
          Wrap / Unwrap SOL
        </a>
      </div>
    </aside>

    <!-- Content -->
    <main class="flex-1 min-w-0 p-2 sm:p-4 md:p-6 overflow-hidden">
      {@render children()}
    </main>
  </div>
</div>
